import * as yup from 'yup'

import { ActivityType } from '../../TimeTrackingOverviewWidget/const'
import {
  PRODUCT_TYPES,
  WORK_DURATION_CONSTRAINTS,
  MAX_PREDEFINED_TASKS,
  DESCRIPTION_MAX_LENGTH,
  INTERNAL_NOTE_MAX_LENGTH,
} from '../constants'

export const createValidationSchema = (isLeader = false) => {
  return yup
    .object({
      users: yup
        .array()
        .of(
          yup.object({
            label: yup.string().required(),
            value: yup.string().required(),
          }),
        )
        .min(1, 'At least one user is required')
        .required('User is required'),
      customer: yup.string().required('Customer is required'),
      order: yup.string().required('Order is required'),
      workPackage: yup.string().required('Work Package is required'),
      workDuration: yup
        .number()
        .typeError('Work Duration must be a number')
        .min(
          WORK_DURATION_CONSTRAINTS.MIN,
          `Minimum duration is ${WORK_DURATION_CONSTRAINTS.MIN} hours`,
        )
        .max(
          WORK_DURATION_CONSTRAINTS.MAX,
          `Maximum duration is ${WORK_DURATION_CONSTRAINTS.MAX} hours`,
        )
        .required('Work Duration is required')
        .test(
          'is-half-hour-increment',
          `Duration must be in ${WORK_DURATION_CONSTRAINTS.INCREMENT} hour increments (e.g., 0.5, 1, 1.5, 2, 2.5)`,
          (value) => {
            if (value == null) return false
            return (value * (1 / WORK_DURATION_CONSTRAINTS.INCREMENT)) % 1 === 0
          },
        ),
      billedDuration: yup
        .number()
        .typeError('Billed duration must be a number')
        .min(0, 'Billed duration cannot be negative')
        .max(
          WORK_DURATION_CONSTRAINTS.MAX,
          `Maximum duration is ${WORK_DURATION_CONSTRAINTS.MAX} hours`,
        )
        .test(
          'is-half-hour-increment',
          `Billed duration must be in ${WORK_DURATION_CONSTRAINTS.INCREMENT} hour increments`,
          (value) => {
            if (value == null) return true
            return (value * (1 / WORK_DURATION_CONSTRAINTS.INCREMENT)) % 1 === 0
          },
        )
        .required('Billed duration is required'),
      typeOfActivity: yup.string().required('Type of Activity is required'),
      productType: yup.string().when('typeOfActivity', {
        is: ActivityType.SUPPORT,
        then: (schema) =>
          schema.required('Product Type is required').oneOf(
            PRODUCT_TYPES.map((p) => p.value),
            'Invalid Product Type',
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      description: yup
        .string()
        .trim('Description cannot contain only whitespace')
        .test(
          'total-description-length',
          `Total description (including selected tasks) cannot exceed ${DESCRIPTION_MAX_LENGTH} characters`,
          function (value) {
            const { toggledTasks } = this.parent
            const descriptionLength = value ? value.length : 0
            const tasksLength =
              toggledTasks.length > 0
                ? toggledTasks.join(',').concat(';').length
                : 0
            const totalLength = descriptionLength + tasksLength

            return totalLength <= DESCRIPTION_MAX_LENGTH
          },
        ),
      toggledTasks: yup
        .array()
        .of(yup.string())
        .max(
          MAX_PREDEFINED_TASKS,
          `Maximum ${MAX_PREDEFINED_TASKS} predefined tasks allowed`,
        ),
      internalNote: yup
        .string()
        .trim('Internal Note cannot contain only whitespace')
        .max(
          INTERNAL_NOTE_MAX_LENGTH,
          `Internal Note cannot exceed ${INTERNAL_NOTE_MAX_LENGTH} characters`,
        ),
    })
    .test(
      'billable-requires-billed-duration',
      'Billable entries must have a billed duration greater than 0',
      function (value) {
        const { typeOfActivity, billedDuration } = value

        if (
          isLeader &&
          typeOfActivity === ActivityType.BILLABLE &&
          (!billedDuration || billedDuration <= 0)
        ) {
          return this.createError({
            path: 'billedDuration',
            message:
              'Billable entries require a billed duration greater than 0 — set it, or choose a non-billable activity type',
          })
        }

        return true
      },
    )
    .test(
      'description-or-tasks',
      'Either provide a description or select predefined tasks (max 5)',
      function (value) {
        const { description, toggledTasks } = value
        const hasDescription = description && description.trim().length > 0
        const hasTasks = toggledTasks && toggledTasks.length > 0

        if (hasDescription || hasTasks) {
          return true
        }

        return this.createError({
          path: 'description',
          message: 'Either provide a description or select predefined tasks',
        })
      },
    )
}
